import { useEffect, useRef, useState, useCallback } from "react";
import { wsUrl } from "@/lib/api";

// Realtime WebSocket with auto-reconnect. Each effect run owns its own socket
// via local variables (not shared refs), so React StrictMode / reconnects can
// never leave a stale socket alive and duplicate every incoming message.
export function useRealtime({ token, conversationId, enabled = true, handlers = {} }) {
  const wsRef = useRef(null);
  const handlersRef = useRef(handlers);
  const [connected, setConnected] = useState(false);

  handlersRef.current = handlers;

  const send = useCallback((data) => {
    const ws = wsRef.current;
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(data));
      return true;
    }
    return false;
  }, []);

  useEffect(() => {
    if (!enabled || !token) return;

    let stopped = false;
    let ws = null;
    let reconnectTimer = null;

    const connect = () => {
      if (stopped) return;
      const params = { token };
      if (conversationId) params.conversation_id = conversationId;
      ws = new WebSocket(wsUrl(params));
      wsRef.current = ws;

      ws.onopen = () => setConnected(true);
      ws.onclose = () => {
        setConnected(false);
        if (!stopped) reconnectTimer = setTimeout(connect, 2000);
      };
      ws.onerror = () => {
        try { ws.close(); } catch {}
      };
      ws.onmessage = (evt) => {
        let data;
        try {
          data = JSON.parse(evt.data);
        } catch {
          return;
        }
        const h = handlersRef.current;
        switch (data.type) {
          case "message":
            h.onMessage && h.onMessage(data.message);
            break;
          case "conversation_update":
            h.onConversationUpdate && h.onConversationUpdate(data.conversation);
            break;
          case "conversation_resolved":
            h.onResolved && h.onResolved(data);
            break;
          case "message_update":
            h.onMessageUpdate && h.onMessageUpdate(data.message);
            break;
          case "agent_presence":
            h.onPresence && h.onPresence(data);
            break;
          case "typing":
            h.onTyping && h.onTyping(data);
            break;
          case "call-request":
          case "call-accept":
          case "call-reject":
          case "call-end":
          case "webrtc":
            h.onSignal && h.onSignal(data);
            break;
          default:
            break;
        }
      };
    };

    connect();

    // Heartbeat so the server keeps the agent marked online (presence/routing).
    const heartbeat = setInterval(() => {
      const w = wsRef.current;
      if (w && w.readyState === WebSocket.OPEN) {
        try { w.send(JSON.stringify({ type: "ping" })); } catch {}
      }
    }, 25000);

    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      clearInterval(heartbeat);
      if (ws) {
        // Detach handlers so this socket's onclose can't schedule a reconnect.
        ws.onopen = null;
        ws.onclose = null;
        ws.onerror = null;
        ws.onmessage = null;
        try { ws.close(); } catch {}
        if (wsRef.current === ws) wsRef.current = null;
      }
    };
  }, [token, conversationId, enabled]);

  return { send, connected };
}
